import { useState } from "react";
import { formatNumber } from "../utils/format";

interface TokenPoint {
  time: string;
  end: string;
  total_tokens: number;
}

interface TokenChartProps {
  data?: TokenPoint[];
  duration: string;
  loading?: boolean;
}

const BAR_HEIGHT = 160;

function formatTime(iso: string, duration: string): string {
  const d = new Date(iso);
  if (duration === "1y") return d.toLocaleString("fa-IR", { month: "short" });
  if (duration === "1w")
    return d.toLocaleString("fa-IR", { day: "numeric", month: "short" });
  if (duration === "1mo") return d.toLocaleString("fa-IR", { day: "numeric" });
  return d.toLocaleString("fa-IR", { hour: "2-digit", minute: "2-digit" });
}

function formatRange(start: string, end: string, duration: string): string {
  const s = new Date(start);
  const e = new Date(end);
  if (duration === "1y") return s.toLocaleString("fa-IR", { month: "long", year: "numeric" });
  if (duration === "1w" || duration === "1mo")
    return s.toLocaleString("fa-IR", { day: "numeric", month: "long" });
  return `${s.toLocaleString("fa-IR", { hour: "2-digit", minute: "2-digit" })} — ${e.toLocaleString("fa-IR", { hour: "2-digit", minute: "2-digit" })}`;
}

function niceMax(max: number): number {
  if (max <= 0) return 1;
  const pow = Math.pow(10, Math.floor(Math.log10(max)));
  const frac = max / pow;
  let nice = 10;
  if (frac <= 1) nice = 1;
  else if (frac <= 2) nice = 2;
  else if (frac <= 2.5) nice = 2.5;
  else if (frac <= 5) nice = 5;
  return nice * pow;
}

export default function TokenChart({ data, duration, loading }: TokenChartProps) {
  const [hovered, setHovered] = useState<number | null>(null);
  const points = data || [];
  const maxRaw = points.reduce((m, p) => Math.max(m, p.total_tokens), 0);
  const max = niceMax(maxRaw);
  const ticks = [0, 1, 2, 3, 4].map((i) => (max * i) / 4);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-48">
        <svg className="animate-spin h-6 w-6 text-primary" viewBox="0 0 24 24" fill="none">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
        </svg>
      </div>
    );
  }

  if (!points.length) {
    return (
      <div className="flex items-center justify-center h-48 text-text-tertiary">
        <p className="text-body">داده‌ای برای نمایش وجود ندارد</p>
      </div>
    );
  }

  return (
    <div className="pt-8" dir="ltr">
      <div className="flex gap-3">
        {/* Y axis */}
        <div className="relative w-16 shrink-0" style={{ height: BAR_HEIGHT }}>
          {ticks.map((t) => (
            <span
              key={t}
              dir="rtl"
              className="absolute right-1 -translate-y-1/2 text-[10px] text-text-tertiary"
              style={{ top: `${(1 - t / max) * 100}%` }}
            >
              {formatNumber(t)}
            </span>
          ))}
        </div>

        {/* Chart area */}
        <div className="relative flex-1" style={{ height: BAR_HEIGHT }}>
          {ticks.map((t) => (
            <div
              key={t}
              className="absolute left-0 right-0"
              style={{ top: `${(1 - t / max) * 100}%`, height: 1, background: "rgb(var(--color-border))" }}
            />
          ))}

          <div className="absolute inset-0 flex items-end gap-1">
            {points.map((p, i) => {
              const height = max > 0 ? (p.total_tokens / max) * 100 : 0;
              const barHeight = p.total_tokens > 0 ? Math.max(height, 3) : 1.5;
              const isHovered = hovered === i;
              return (
                <div
                  key={i}
                  className="relative flex-1 h-full flex flex-col items-center justify-end"
                  onMouseEnter={() => setHovered(i)}
                  onMouseLeave={() => setHovered(null)}
                >
                  {isHovered && (
                    <div
                      dir="rtl"
                      className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 z-20 px-3 py-2 rounded-btn whitespace-nowrap text-center"
                      style={{ background: "rgba(26,26,26,0.92)", color: "#fff", boxShadow: "0 8px 24px rgba(0,0,0,0.18)" }}
                    >
                      <p className="text-[11px]" style={{ opacity: 0.8 }}>{formatRange(p.time, p.end, duration)}</p>
                      <p className="text-xs font-semibold mt-0.5" style={{ color: "#22B06E" }}>
                        {formatNumber(p.total_tokens)} توکن
                      </p>
                    </div>
                  )}
                  <div
                    className="w-full rounded-t-[4px] transition-all duration-150"
                    style={{
                      height: `${barHeight}%`,
                      background: isHovered
                        ? "linear-gradient(180deg, #22B06E 0%, #167A41 100%)"
                        : "linear-gradient(180deg, #22B06E 0%, #1D9C51 100%)",
                      opacity: hovered !== null && !isHovered ? 0.45 : 1,
                      boxShadow: isHovered ? "0 4px 14px rgba(34,176,110,0.4)" : "none",
                    }}
                  />
                </div>
              );
            })}
          </div>
        </div>
      </div>
      {/* X-axis labels in a dedicated row so bars stay bottom-aligned */}
      <div className="flex gap-1 mt-1.5 ml-[76px]">
        {points.map((p, i) => (
          <div key={i} dir="rtl" className="flex-1 text-center text-[10px] text-text-tertiary truncate">
            {formatTime(p.time, duration)}
          </div>
        ))}
      </div>
    </div>
  );
}
